import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyEmail, resendVerification } from '../services/auth';
import { useAppStore } from '../store/useAppStore';

type Status = 'pending' | 'consuming' | 'success' | 'error';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAppStore();
  const email: string = (location.state as any)?.email ?? '';

  const [status, setStatus] = useState<Status>('pending');
  const [errorMsg, setErrorMsg] = useState('');
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  // If the page loads with ?token= in the URL, consume it immediately.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) return;

    setStatus('consuming');
    verifyEmail(token)
      .then((authResult) => {
        setStatus('success');
        if (authResult) {
          // Fresh token returned — log the user in directly, skip /login.
          login(authResult.token, authResult.role, authResult.profileId, authResult.firstName, authResult.displayName, authResult.email);
          setTimeout(() => navigate('/', { replace: true }), 1500);
        } else {
          // Backend didn't return a token (rare) — fall back to /login.
          setTimeout(() => navigate('/login', { replace: true }), 2500);
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg('This verification link has expired or already been used. Request a new one below.');
      });
  }, []);

  async function handleResend() {
    setResending(true);
    setResent(false);
    try {
      await resendVerification();
      setResent(true);
    } catch {
      setErrorMsg('Could not resend. Please try again in a moment.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={s.outer}>
      <div style={s.card}>
        <Brand />

        {status === 'consuming' && (
          <>
            <div style={s.icon}>⏳</div>
            <h1 style={s.heading}>Verifying your email…</h1>
            <p style={s.sub}>Just a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={s.icon}>✅</div>
            <h1 style={s.heading}>Email verified!</h1>
            <p style={s.sub}>Your workspace is ready. Taking you to sign in…</p>
          </>
        )}

        {(status === 'pending' || status === 'error') && (
          <>
            <div style={s.icon}>✉️</div>
            <h1 style={s.heading}>Check your inbox</h1>
            <p style={s.sub}>
              {email
                ? <>We sent a verification link to <strong>{email}</strong>.<br />Click the link to activate your workspace.</>
                : <>We sent a verification link to your email.<br />Click the link to activate your workspace.</>}
            </p>

            {errorMsg && <p style={s.error}>{errorMsg}</p>}

            {resent && <p style={s.success}>Verification email resent — check your inbox.</p>}

            <button
              style={{ ...s.btn, opacity: resending ? 0.7 : 1, marginTop: 20 }}
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>

            <button style={s.textBtn} onClick={() => navigate('/login')}>
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="9" cy="7" r="4" stroke="white" strokeWidth="2"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink, #111)', letterSpacing: '-0.3px' }}>SK People</span>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  outer:   { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f5f7)', padding: 16 },
  card:    { background: 'var(--surface, #fff)', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' },
  icon:    { fontSize: 44, marginBottom: 16 },
  heading: { fontSize: 24, fontWeight: 700, color: 'var(--ink, #111)', margin: '0 0 8px' },
  sub:     { fontSize: 14, color: 'var(--ink-3, #888)', margin: '0 0 4px', lineHeight: 1.7 },
  error:   { fontSize: 13, color: '#dc2626', margin: '12px 0 0' },
  success: { fontSize: 13, color: '#16a34a', margin: '12px 0 0' },
  btn:     { width: '100%', padding: '11px 0', borderRadius: 8, background: 'var(--accent, #4F46E5)', color: '#fff', fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' },
  textBtn: { display: 'block', width: '100%', marginTop: 14, background: 'none', border: 'none', color: 'var(--ink-3, #888)', fontSize: 13, cursor: 'pointer' },
};
